import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import type { RegisteredMember } from '../types/member';
import { db } from '../services/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { PencilSquareIcon, PrinterIcon } from '@heroicons/react/24/outline';
import { Modal } from '../components/common/Modal';
import { MemberForm } from '../components/members/MemberForm';
import PersonalInfo from '../components/members/PersonalInfo';
import FamilyInfo from '../components/members/FamilyInfo';
import VisitationHistory from '../components/members/VisitationHistory';
import DiscipleshipStatus from '../components/members/DiscipleshipStatus';
import PrintableReport from '../components/members/PrintableReport';

const MemberDetailPage: React.FC = () => {
  const { memberId } = useParams<{ memberId: string }>();
  const [member, setMember] = useState<RegisteredMember | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('personalInfo');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchMember = useCallback(async () => {
    if (!memberId) {
      setError('성도 ID가 제공되지 않았습니다.');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const memberDocRef = doc(db, 'members', memberId);
      const memberDoc = await getDoc(memberDocRef);
      
      if (memberDoc.exists()) {
        setMember({ id: memberDoc.id, ...memberDoc.data() } as RegisteredMember);
      } else {
        setError('성도 정보를 찾을 수 없습니다.');
      }
    } catch (err) {
      console.error('성도 정보 조회 중 오류 발생:', err);
      if (err instanceof Error && err.message?.includes('offline')) {
        setError('네트워크 연결이 불안정합니다. 오프라인 모드로 전환됩니다.');
      } else {
        setError('성도 정보를 불러오는 중 오류가 발생했습니다.');
      }
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => {
    fetchMember();
  }, [fetchMember]);

  const handleUpdateMember = async () => {
    setIsModalOpen(false);
    await fetchMember(); // Re-fetch member data to show updated info
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <div className="text-red-500 text-center mt-4">{error}</div>;
  }

  if (!member) {
    return <div className="text-center mt-4">성도 정보가 없습니다.</div>;
  }

  const renderTabContent = () => {
    if (!member) return null;

    switch (activeTab) {
      case 'personalInfo':
        return <PersonalInfo member={member} />;
      case 'familyInfo':
        return <FamilyInfo memberId={member.id} />;
      case 'visitationHistory':
        return <VisitationHistory memberId={member.id} />;
      case 'discipleshipStatus':
        return <DiscipleshipStatus memberId={member.id} />;
      default:
        return null;
    }
  };

  return (
    <>
      <div className="no-print p-4 sm:p-6 bg-gray-50 min-h-screen">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white p-6 rounded-lg shadow-md mb-6">
              <div className="flex flex-col sm:flex-row items-center">
                  {/* Profile picture placeholder */}
                <div className="w-24 h-24 bg-gray-300 rounded-full mr-6 flex-shrink-0">
                    {/* You can add an <img> tag here later */}
                </div>
                <div className="flex-grow">
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">{member.name}</h1>
                    <p className="text-md text-gray-600">{member.position || '성도'}</p>
                    <p className="text-sm text-gray-500 mt-1">{member.cellGroup || '소속 없음'}</p>
                </div>
                <div className="no-print flex flex-col sm:flex-row gap-2 mt-4 sm:mt-0">
                    <button 
                        onClick={() => setIsModalOpen(true)}
                        className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-semibold shadow"
                    >
                        <PencilSquareIcon className="h-5 w-5 mr-2" />
                        수정
                    </button>
                    <button 
                        onClick={() => window.print()}
                        className="flex items-center justify-center px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors text-sm font-semibold shadow"
                    >
                        <PrinterIcon className="h-5 w-5 mr-2" />
                        프린트
                    </button>
                </div>
              </div>
          </div>

          <div className="bg-white rounded-lg shadow-md printable-area">
              <div className="no-print bg-gray-100 shadow-md border-b border-gray-300 rounded-t-lg">
                  <nav className="-mb-px flex space-x-6 sm:space-x-8 px-6" aria-label="Tabs">
                  <button
                    onClick={() => setActiveTab('personalInfo')}
                    className={`whitespace-nowrap py-4 px-1 border-b-2 font-semibold text-base transition-colors duration-200 ${
                    activeTab === 'personalInfo'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                    개인신상
                </button>
                <button
                    onClick={() => setActiveTab('familyInfo')}
                    className={`whitespace-nowrap py-4 px-1 border-b-2 font-semibold text-base transition-colors duration-200 ${
                    activeTab === 'familyInfo'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                    가족사항
                </button>
                <button
                    onClick={() => setActiveTab('visitationHistory')}
                    className={`whitespace-nowrap py-4 px-1 border-b-2 font-semibold text-base transition-colors duration-200 ${
                    activeTab === 'visitationHistory'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                    심방기록
                </button>
                <button
                    onClick={() => setActiveTab('discipleshipStatus')}
                    className={`whitespace-nowrap py-4 px-1 border-b-2 font-semibold text-base transition-colors duration-200 ${
                    activeTab === 'discipleshipStatus'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                    양육현황
                </button>
                </nav>
            </div>

              <div className="p-6">
                  {renderTabContent()}
              </div>
          </div>
        </div>
      </div>
      {member && <PrintableReport member={member} />}
      
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="성도 정보 수정">
        {member && (
          <MemberForm
            onSuccess={handleUpdateMember}
            editingMember={member}
          />
        )}
      </Modal>
    </>
  );
};

export default MemberDetailPage;
